import { useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import { projects } from "../data/site";
import { Develop, Reveal, useMatchMedia } from "./motion";

export default function Work() {
  const [active, setActive] = useState(null);
  const reduced = useReducedMotion();
  const listRef = useRef(null);
  // No pointer to hold the print with, so the print goes in the row instead.
  const compact = useMatchMedia("(max-width: 899px), (hover: none)");

  // The print follows the pointer with a lag, so it reads as a held object
  // rather than a tooltip nailed to the cursor.
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const spring = { stiffness: 220, damping: 28, mass: 0.7 };
  const px = useSpring(x, spring);
  const py = useSpring(y, spring);

  // Held to the right of the cursor so it never covers the title being read,
  // and pulled back in before it can run off the edge of the window.
  const track = (event) => {
    const half = Math.min(window.innerWidth * 0.12, 176);
    x.set(Math.min(event.clientX + 190, window.innerWidth - half - 32));
    y.set(event.clientY);
  };

  return (
    <section className="section work" id="work">
      <div className="shell">
        <Reveal>
          <p className="label">Selected work</p>
          <h2 className="h2 work__heading">
            Things that exist because I built them.
          </h2>
        </Reveal>

        <ul className="work__list" ref={listRef} onMouseMove={track}>
          {projects.map((project, i) => {
            return (
              <li
                key={project.title}
                className={`work__row ${active === i ? "is-active" : ""} ${
                  active !== null && active !== i ? "is-dimmed" : ""
                }`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
              >
                {/* The title carries the primary link and stretches over the
                    whole row, so a second link can sit beside it without one
                    anchor nesting inside another. */}
                <div className="work__link">
                  <span className="label work__index">
                    {String(i + 1).padStart(2, "0")}
                  </span>

                  <span className="work__body">
                    {project.href ? (
                      <a
                        className="work__title work__primary"
                        href={project.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {project.title}
                      </a>
                    ) : (
                      <span className="work__title">{project.title}</span>
                    )}
                    <span className="work__summary soft">{project.summary}</span>
                    {project.stack.length > 0 && (
                      <span className="work__stack">
                        {project.stack.map((tech) => (
                          <span className="label work__tech" key={tech}>
                            {tech}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>

                  <span className="work__meta label">
                    {project.year}
                    {project.repo && (
                      <a
                        className="work__repo"
                        href={project.repo}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Code
                      </a>
                    )}
                    {project.href && (
                      <span className="work__arrow" aria-hidden="true">
                        ↗
                      </span>
                    )}
                  </span>
                </div>

                {compact && project.image && (
                  <div className="work__inline">
                    <Develop src={project.image} alt={project.alt} className="work__inline-img" />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {!reduced && !compact && (
        <AnimatePresence>
          {active !== null && projects[active].image && (
            <motion.div
              className="work__print"
              style={{ x: px, y: py }}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              aria-hidden="true"
            >
              <motion.img
                key={projects[active].image}
                src={projects[active].image}
                alt=""
                initial={{ filter: "saturate(0.05) brightness(0.5) contrast(1.5)" }}
                animate={{ filter: "saturate(1) brightness(1) contrast(1)" }}
                transition={{ duration: 0.75, ease: "easeOut" }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </section>
  );
}
